
import "./index.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { Suspense, useEffect, useState } from "react";
import { useIsAuthenticated, useMsal } from "@azure/msal-react";
import { SSOSignin } from "./components/signin/SSOSignin";
import { Layout } from "./components/constants/Layout";
import { Dashboard } from "./components/modules/Dashboard";      // first page after login: kept in the main file
import { setAxiosDefauls } from "./middleware/interceptor";
import { Timesheetapi } from "./middleware/interceptor";
import PrivateRoute from './PrivateRoute'
import { lazyWithPreload, preloadAllPages } from "./utils/lazyWithPreload";   // SITE LOAD

// ── SITE LOAD: every other page in its own file, downloaded in the background after login ──
const named = (load, name, key) => lazyWithPreload(() => load().then((m) => ({ default: m[name] })), key);
const Timesheet = named(() => import("./components/modules/Timesheet"), "Timesheet", "timesheet");
const MyWorkItems = named(() => import("./components/modules/MyWorkItems"), "MyWorkItems", "workitems");
const Teamtimesheet = named(() => import("./components/modules/Teamtimesheet"), "Teamtimesheet", "teamtimesheet");
const Employee = named(() => import("./components/modules/Settings/Employee"), "Employee", "employee");
const Project = named(() => import("./components/modules/Settings/Project"), "Project", "projects");
const Client = named(() => import("./components/modules/Settings/Clients"), "Client", "clients");
const Employee_report = named(() => import("./components/modules/Reports/Employee_report"), "Employee_report", "employee-report");
const ProjectReports = lazyWithPreload(() => import("./components/modules/Reports/ProjectReport"), "project-report");
const ClientAllocation = lazyWithPreload(() => import("./components/modules/Reports/ClientAllocation"), "client-report");
const LoginInfo = named(() => import("./components/Logins/LoginInfo"), "LoginInfo", "logininfo");
const Task = named(() => import("./components/modules/Settings/Task"), "Task", "ticket");
const ViewTicket = lazyWithPreload(() => import("./components/sections/ViewTicket"), "view-ticket");
const Approvals = named(() => import("./components/modules/Settings/Approvals"), "Approvals", "approvals");
const TeamsActivities = lazyWithPreload(() => import("./components/modules/TeamsActivities"), "teams-activities");
const Users = lazyWithPreload(() => import("./components/modules/Users"), "users");
const Role = lazyWithPreload(() => import("./components/modules/Role"), "role");
const Utilization = named(() => import("./components/modules/Utilization"), "Utilization", "utilization");
const HolidayCalendar = named(() => import("./components/modules/Holidaycalendar"), "HolidayCalendar", "holidays");
const DealDesk = named(() => import("./components/modules/Dealdesk"), "DealDesk", "deal-desk");
const OrgChart = named(() => import("./components/modules/Orgchart"), "OrgChart", "org-chart");
// (Test was imported before but never used: removed)

// Inside the layout, so the sidebar and header stay on screen. Only shows if a page file isn't downloaded yet.
const PageLoading = () => (
  <div role="status" aria-live="polite" style={{ display: "flex", alignItems: "center", justifyContent: "center", minHeight: "50vh", color: "#5B6474", fontSize: 14 }}>
    Loading…
  </div>
);
const L = (node) => <Suspense fallback={<PageLoading />}>{node}</Suspense>;

setAxiosDefauls();



function App() {
  const isAuthenticated = useIsAuthenticated();
  const { instance } = useMsal();
  const [Emp_Role, setEmp_Role] = useState()
  const [employee_id, setEmployee_id] = useState();
  const [Secret_key, setSecret_key] = useState("");
  const [sidebor, setsidebor] = useState()

  // SITE LOAD: AWS.config.update(...) removed — no AWS SDK in the browser any more

  useEffect(() => {
    const fetchData = async () => {
      if (isAuthenticated) {
        const { username } = instance?.getActiveAccount();

        if (
          localStorage.getItem("bearer-token") !== undefined &&
          localStorage.getItem("bearer-token") !== null &&
          isAuthenticated
        ) {
          // SITE LOAD: employee (role) first — the menu and role pages depend on it
          const emp = await getemployee(username);
          // Download the other pages in the background (idle time only; safe to call again)
          preloadAllPages();
          // Encryption key for the client report: only roles that open it, and without blocking anything
          if (emp && ["2", "3", "6"].includes(String(emp.employee_role_id))) getEncryptionKey(emp.employee_id);
        }
      }
    };

    fetchData();

  }, [isAuthenticated, localStorage.getItem("bearer-token")]);

  // SITE LOAD: replaces getkey() + getSecretValue() (aws-sdk Secrets Manager in the browser)
  const getEncryptionKey = async (empid) => {
    try {
      const res = await Timesheetapi.get(`api/AppKeys/EncryptionKey?empid=${empid}`);
      if (res?.data?.status === 1) setSecret_key(res.data.key);
      else console.warn("Encryption key not available:", res?.data?.message);
    } catch (error) {
      console.error("Error fetching encryption key:", error);
    }
  };

  const getemployee = async (username) => {
    try {
      const response = await Timesheetapi.get(`api/Projects/getemployeebyid/${username}/`);
      const emp = response?.data?.Employee?.[0];

      setEmp_Role(emp?.employee_role_id);
      setEmployee_id(emp?.employee_id);

      localStorage.setItem('employee_id', emp?.employee_id);
      localStorage.setItem('emp_role_id', emp?.employee_role_id);
      localStorage.setItem('role_name', emp?.employee_role_name);
      localStorage.setItem('Emp_Assign_client', emp?.employee_client);
      localStorage.setItem('Emp_Assign_project', emp?.employee_project);
      return emp;
    } catch (error) {
      console.error("Error fetching employee data: ", error);
      return null;
    }
  };

  return (
    <BrowserRouter>
      <Routes>
        {/* Public login page */}
        <Route
          path="/"
          element={
            <SSOSignin
              isAuthenticated={isAuthenticated}
              setEmp_Role={setEmp_Role}
              Emp_Role={Emp_Role}
            />
          }
        />

        {/* Protected Routes */}
        <Route
          path="/"
          element={
            <PrivateRoute>
              <Layout isAuthenticated={isAuthenticated} setsidebor={setsidebor} />
            </PrivateRoute>
          }
        >
          {/* Common Routes — every role */}
          <Route path="dashboard" element={<Dashboard />} />
          <Route path="timesheets" element={L(<Timesheet isAuthenticated={isAuthenticated} employee_id={employee_id} />)} />
          <Route path="my-workitems" element={L(<MyWorkItems />)} />
          <Route path="teams-activities" element={L(<TeamsActivities />)} />
          <Route path="users" element={L(<Users />)} />
          <Route path="role" element={L(<Role />)} />
          {/* Reached from Admin view; the API also limits data to what each role may see */}
          <Route path="utilization" element={L(<Utilization />)} />
          <Route path="holidays" element={L(<HolidayCalendar />)} />
          {/* Deal Desk: access checked by the API (Web.config DealDesk.Users / DealDesk.Admins) */}
          <Route path="deal-desk" element={L(<DealDesk view="deals" />)} />
          <Route path="deal-desk/admin" element={L(<DealDesk view="admin" />)} />
          {/* Org Chart: everyone can view; Admin / HR (roles 3, 4) can edit (checked by the API too) */}
          <Route path="org-chart" element={L(<OrgChart />)} />
          <Route path="Approvals" element={L(<Approvals />)} />

          {/* Role-Based Routes */}
          {localStorage.getItem("emp_role_id") === "2" && (
            <>
              <Route path="Settings/Teamtimesheet" element={L(<Teamtimesheet isAuthenticated={isAuthenticated} employee_id={employee_id} />)} />
              <Route path="Settings/projects" element={L(<Project isAuthenticated={isAuthenticated} />)} />
              <Route path="Settings/employee" element={L(<Employee isAuthenticated={isAuthenticated} setEmployee_id={setEmployee_id} setEmp_Role={setEmp_Role} />)} />
              <Route path="Settings/clients" element={L(<Client />)} />

              <Route path="Reports/client-report" element={L(<ClientAllocation Secret_key={Secret_key} sidebor={sidebor} />)} />
              <Route path="Reports/Employee_report" element={L(<Employee_report isAuthenticated={isAuthenticated} sidebo={sidebor} />)} />
              <Route path="reports" element={L(<ProjectReports sidebor={sidebor} />)} />
            </>
          )}

          {localStorage.getItem("emp_role_id") === "3" && (
            <>
              <Route path="Reports/client-report" element={L(<ClientAllocation Secret_key={Secret_key} sidebor={sidebor} />)} />
              <Route path="Reports/Employee_report" element={L(<Employee_report isAuthenticated={isAuthenticated} sidebo={sidebor} />)} />
              <Route path="reports" element={L(<ProjectReports sidebor={sidebor} />)} />
              <Route path="Settings/ticket" element={L(<Task />)} />
              <Route path="view-ticket/:clientId" element={L(<ViewTicket />)} />
              <Route path="Settings/projects" element={L(<Project isAuthenticated={isAuthenticated} />)} />
              <Route path="Settings/clients" element={L(<Client />)} />
            </>
          )}

          {localStorage.getItem("emp_role_id") === "4" && (
            <Route path="Logins/LoginInfo" element={L(<LoginInfo />)} />
          )}

          {localStorage.getItem("emp_role_id") === "5" && (
            <>
              <Route path="Logins/LoginInfo" element={L(<LoginInfo />)} />
              <Route path="project-reports" element={L(<ProjectReports />)} />
            </>
          )}

          {localStorage.getItem("emp_role_id") === "6" && (
            <>
              <Route path="Reports/client-report" element={L(<ClientAllocation Secret_key={Secret_key} sidebor={sidebor} />)} />
              <Route path="Reports/Employee_report" element={L(<Employee_report isAuthenticated={isAuthenticated} sidebo={sidebor} />)} />
              <Route path="reports" element={L(<ProjectReports sidebor={sidebor} />)} />
              <Route path="Settings/projects" element={L(<Project isAuthenticated={isAuthenticated} />)} />
              <Route path="Settings/employee" element={L(<Employee isAuthenticated={isAuthenticated} setEmployee_id={setEmployee_id} setEmp_Role={setEmp_Role} />)} />
              <Route path="Settings/clients" element={L(<Client />)} />
              <Route path="Settings/ticket" element={L(<Task />)} />
              <Route path="view-ticket/:clientId" element={L(<ViewTicket />)} />
            </>
          )}
        </Route>

      </Routes>


    </BrowserRouter >
  );
}

export default App;